import { useState, useEffect, useContext, useMemo } from "react";
import { getDomain, getDomainPort } from "@/utils/route-utils";
import type { Card as CardType } from "@stackje/common/config";
import Card from "@stackje/shared-components/cards/Card";
import { Button } from "@stackje/shared-components/ui/button";
import { ScaledPreviewContainer } from "@stackje/shared-components/ui/scaled-preview-container";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { BetweenHorizontalStart, FilePlus, Settings, SquareArrowOutUpRight, SquarePlus, Sparkles } from "lucide-react";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useCardScrollSnap } from "@/hooks/ui/useCardScrollSnap";
import { BottomBar } from "./shared/BottomBar";
import { TopBar } from "./shared/TopBar";
import { UserAccountPopover } from "./shared/UserAccountPopover";
import { ScrollIndicator } from "./shared/ScrollIndicator";
import type { Site } from "@/types/site.types";
import { ApiClient } from "@/lib/apiClient";
import { siteTemplates, createConfigFromTemplate } from "@/utils/site-templates";
import { useSiteUpdateMutation, useSiteQuery, useInvalidateSiteCache } from "@/hooks/useSiteQuery";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import { ColorScheme } from '@stackje/common/colorScheme';
import { generateNewColors } from '@/utils/color-scheme-utils';
import { publishSite } from '@/utils/publish-utils';
import { discardSite } from '@/utils/discard-utils';
import clsx from "clsx";
import { ColorSchemeContext } from "@/context/ColorSchemeContext";
import { PublishStatusBadge } from './shared/PublishStatusBadge';
import { useQuery } from '@tanstack/react-query';
import { builderStartQueryKey, getBuilderStart } from '@/features/builder/api/startApi';

export const DashboardPage = ({ site }: { site: Site }) => {
  const navigate = useNavigate();
  const router = useRouter();
  const { toast } = useToast();
  const { user } = useAuth();
  const updateSiteMutation = useSiteUpdateMutation();
  const invalidateSiteCache = useInvalidateSiteCache();
  const scrollCardHeight = window.visualViewport?.height || window.innerHeight;
  const safeBottom = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom')
  );
  const effectiveScrollCardHeight = scrollCardHeight - safeBottom;

  // Use reactive site data from query to get immediate updates
  const { data: reactiveSiteData } = useSiteQuery(user?.id);
  const currentSite = reactiveSiteData || site; // Use reactive data when available, fallback to prop
  const cards: CardType[] = currentSite.config?.pages?.[0]?.cards ?? [];
  const { colorSchemeId, customColors } = useContext(ColorSchemeContext)
  const currentSchemeColors = useMemo(() => {
    // Use custom colors if available, otherwise use predefined scheme colors
    return colorSchemeId === "custom" && customColors
      ? customColors
      : ColorScheme[colorSchemeId];
  }, [colorSchemeId, customColors]);

  // Owners who also have a builder site get a way back to it; if the builder can't be reached, no link.
  const { data: builderStart } = useQuery({
    queryKey: builderStartQueryKey,
    queryFn: () => getBuilderStart().catch(() => null),
    staleTime: 60_000,
  });

  // Template selection state
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);

  // Custom domain state
  const [customDomainActive, setCustomDomainActive] = useState(false);
  const [customDomainUrl, setCustomDomainUrl] = useState<string | null>(null);

  const {
    currentIndex,
    containerRef,
    cardRefs,
    scrollToCard,
  } = useCardScrollSnap({
    totalCards: cards.length
  });

  // Add a navigation handler function
  const handleNavigation = (path: Parameters<typeof navigate>[0]) => {
    navigate(path);
  };

  // Handle template application
  const handleApplyTemplate = async (templateId: string) => {
    if (!user?.id || !currentSite) return;

    setIsApplyingTemplate(true);

    try {
      const template = siteTemplates.find(t => t.id === templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      const templateConfig = createConfigFromTemplate(
        template,
        currentSite.siteDetails?.title || currentSite.subdomain,
        currentSite.siteDetails?.description
      );

      // Apply current color scheme to template cards
      const updatedPages = templateConfig.pages.map(page => ({
        ...page,
        cards: page.cards.map(card => {
          // Generate new colors for this card using the current color scheme
          const newColors = generateNewColors(card.colors, currentSchemeColors);
          return {
            ...card,
            colors: newColors
          };
        })
      }));

      const updatedSiteData = {
        siteDetails: {
          ...currentSite.siteDetails,
        },
        config: {
          ...currentSite.config,
          ...templateConfig,
          pages: updatedPages,
        },
      };

      await updateSiteMutation.mutateAsync({
        siteId: currentSite.id,
        userId: user.id,
        data: updatedSiteData,
      });

      toast({
        description: `${template.name} template has been applied successfully.`,
      });

      setShowTemplateSelection(false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to apply template. Please try again.';
      captureErrorAndToast(error, toast, {
        description: errorMessage
      });
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  // Check custom domain status in background
  useEffect(() => {
    const checkCustomDomain = async () => {
      if (currentSite.customDomain && currentSite.id) {
        try {
          const result = await ApiClient.getCustomDomainStatus(currentSite.id);
          if (result.data && result.data.status === "active") {
            setCustomDomainActive(true);
            setCustomDomainUrl(result.data.domain);
          } else {
            // Reset state if domain is not active anymore
            setCustomDomainActive(false);
            setCustomDomainUrl(null);
          }
        } catch (error) {
          console.error("Failed to check custom domain status:", error);
        }
      }
    };

    // Check immediately on mount and navigation
    checkCustomDomain();

    // Subscribe to router events to re-check on navigation
    const unsubscribe = router.subscribe('onLoad', () => {
      checkCustomDomain();
    });

    return unsubscribe;
  }, [currentSite.customDomain, currentSite.id, router]);

  return (
    <div className="mx-auto">
      <TopBar>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: "/site-details" })}
        >
          <Settings className="h-5 w-5" />
        </Button>
        <div className="flex flex-row items-center space-x-2">
          <PublishStatusBadge
            lastModifiedAt={currentSite.lastModifiedAt ?? null}
            lastPublishedAt={currentSite.lastPublishedAt ?? null}
            canDiscard={currentSite.lastPublishedAt !== null}
            onPublish={async () => {
              try {
                await publishSite(currentSite.id);
                invalidateSiteCache(user!.id);
                toast({ description: "Site published successfully." });
              } catch (error) {
                captureErrorAndToast(error, toast, {
                  description: "Failed to publish site. Please try again.",
                });
              }
            }}
            onDiscard={async () => {
              try {
                await discardSite(currentSite.id);
                invalidateSiteCache(user!.id);
                toast({ description: "Changes discarded." });
              } catch (error) {
                captureErrorAndToast(error, toast, {
                  description: "Failed to discard changes. Please try again.",
                });
              }
            }}
          />
          <a
            href={
              customDomainActive && customDomainUrl
                ? `https://${customDomainUrl}`
                : `${getDomain() === 'localhost' ? "http" : "https"}://${currentSite.subdomain}.${getDomain()}${getDomainPort()}`
            }
            className="flex flex-row items-center space-x-2"
            target="_blank"
            rel="noreferrer"
          >
            <span className="underline decoration-dash">
              {customDomainActive && customDomainUrl ? (
                <span className="font-bold">{customDomainUrl}</span>
              ) : (
                <>
                  <span className="font-bold">{currentSite.subdomain}</span>.{getDomain()}
                </>
              )}
            </span>
            <SquareArrowOutUpRight className="h-4 w-4" />
          </a>
        </div>
        <UserAccountPopover
          builderSiteId={builderStart?.siteId ?? null}
          canStartBuilder={Boolean(builderStart?.available && !builderStart.siteId)}
        />
      </TopBar>

      {/* Card scroll indicator */}
      {cards.length > 1 && (
        <ScrollIndicator
          totalCards={cards.length}
          currentIndex={currentIndex}
          onScrollToCard={scrollToCard}
        />
      )}

      <div 
        ref={containerRef}
        className="overflow-y-auto scroll-smooth scrollbar-hide"
        style={{ 
          height: effectiveScrollCardHeight,
          scrollSnapType: 'y mandatory',
          scrollPadding: '0',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {cards.length === 0 ? (
          <div className={clsx("flex flex-col items-center h-full justify-center empty-state px-6", showTemplateSelection && "mt-4 mb-6 md:my-0")}>
            {!showTemplateSelection ? (
              <>
                <FilePlus className="h-20 w-20 text-gray-400 mb-4" />
                <h2 className="text-2xl mb-2">Add your first card</h2>
                <p className="text-gray-600 text-center mb-6">
                  Get started by adding a card to your site or choose from one of our templates
                </p>

                <div className="flex flex-col space-y-3 w-full max-w-sm">
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: "/add-card", search: { insertAfterIndex: -1 } })}
                    className="flex items-center justify-center space-x-2 py-3"
                  >
                    <SquarePlus className="h-4 w-4" />
                    <span>Add Card</span>
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => setShowTemplateSelection(true)}
                    className="flex items-center justify-center space-x-2 py-3 border border-dashed border-gray-300"
                  >
                    <Sparkles className="h-4 w-4" />
                    <span>Choose Template</span>
                  </Button>
                </div>
              </>
            ) : (
              <div className="w-full max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Choose a Template</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isApplyingTemplate}
                    onClick={() => setShowTemplateSelection(false)}
                  >
                    <Cross2Icon className="h-6 w-6" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {siteTemplates.filter(template => template.id !== 'empty-canvas').map((template) => (
                    <div
                      key={template.id}
                      className={`
                        p-4 border rounded-lg transition-all
                        ${isApplyingTemplate
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer'
                        }
                      `}
                      onClick={() => !isApplyingTemplate && handleApplyTemplate(template.id)}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="text-2xl flex-shrink-0">{template.icon}</div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold mb-1 ${isApplyingTemplate ? 'text-gray-500' : 'text-gray-900'}`}>
                            {template.name}
                          </h3>
                          <p className={`text-sm leading-relaxed ${isApplyingTemplate ? 'text-gray-400' : 'text-gray-600'}`}>
                            {template.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {isApplyingTemplate && (
                  <div className="text-center text-gray-600 py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      <p>Applying template...</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {cards.map((card, index) => (
              <div
                key={index}
                className="flex items-center justify-center flex-shrink-0 w-full"
                style={{ 
                  height: effectiveScrollCardHeight,
                  scrollSnapAlign: 'start',
                  scrollSnapStop: 'always'
                }}
                ref={(el) => {
                  if (el) {
                    cardRefs.current[index] = el;
                  }
                }}
              >
                <div className="relative">
                  <ScaledPreviewContainer
                    interactive={true}
                    onClick={() => navigate({ to: `/edit-card/${index}` })}
                  >
                    <div className="z-50 absolute top-0 left-0 right-0 bg-white bg-opacity-50 p-2 text-center text-lg text-gray-600 pointer-events-none">
                      Tap card to edit
                    </div>

                    <Card isPreview={true} {...card} />
                  </ScaledPreviewContainer>
                </div>
                <Link to="/edit-card/$cardIndex" params={{ cardIndex: index.toString() }} className="hidden">
                  <span />
                </Link>
              </div>
            ))}
          </>
        )}
      </div>

      <BottomBar>
        <Button
          variant="ghost"
          className={`hover:bg-accent hover:opacity-80 relative transition-all w-[50%]`}
          onClick={() => handleNavigation({ to: "/manage-cards" })}
        >
          <BetweenHorizontalStart className="mr-2 h-4 w-4" />
          Manage Cards
        </Button>

        <Button
          variant="ghost"
          className={`hover:bg-accent hover:opacity-80 relative transition-all w-[50%]`}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => handleNavigation({ to: "/add-card", search: { insertAfterIndex: currentIndex } as any })}
        >
          <SquarePlus className="mr-2 h-4 w-4" />
          Add Card{cards.length > 0 ? ` After` : ''}
        </Button>
      </BottomBar>
    </div>
  );
};
